import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { GridRClickDirective } from '@app/shared/directives/grid-r-click/grid-r-click.directive';
import { IProjectCostCodeCostTypeItem } from '@app/shared/interfaces';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogComponent } from '@progress/kendo-angular-dialog';
import { DataStateChangeEvent, GridDataResult, GridModule, RowArgs, SelectionEvent } from '@progress/kendo-angular-grid';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { orderBy, SortDescriptor, State } from '@progress/kendo-data-query';
import { map, Observable } from 'rxjs';
import { GlobalCcctCombination } from '../../enums/global-cttt-combination';
import { CcctGridService } from '../../services/ccct-grid.service';
import { IKeyedPhaseStepSort } from '../project-estimate-detail/project-estimate-detail.component';

@Component({
  selector: 'app-ccct-select',
  templateUrl: './ccct-select.component.html',
  styleUrls: ['./ccct-select.component.scss'],
  imports: [
    AsyncPipe,
    ButtonDirective,
    DialogComponent,
    GridModule,
    GridRClickDirective,
    LoaderComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    CcctGridService,
  ],
})
export class CcctSelectComponent implements OnInit {
  public ccctGridService = inject(CcctGridService);

  public icons = kendoIcons;
  public pageSize: number = 100;
  public view: Observable<GridDataResult>;
  public gridView: GridDataResult = { data: [], total: 0 };
  public state: State = { skip: 0, take: this.pageSize };
  public projectType: number;
  public selectedItems: RowArgs[] = [];
  public selectedPhaseStepKey: string[] = [];
  public phaseStepSort: SortDescriptor[] = [];

  readonly iProjectTypeId = input<number>(undefined);
  readonly iProjectCcctItem = input<IProjectCostCodeCostTypeItem[]>(undefined);
  readonly showDialog = input<boolean>(undefined);
  readonly showPhaseStep = input<boolean>(undefined);
  readonly iDistinctPhaseStep = input<Array<IKeyedPhaseStepSort>>([]);

  readonly addCcctItem = output<{
    costItems: IProjectCostCodeCostTypeItem[];
    phaseSteps: string[];
}>();
  readonly closeDialog = output<any>();

  ngOnInit() {
    this.initializeFilter();
    this.ccctGridService.query(this.state);

    this.view = this.ccctGridService
      .pipe(
        map(x => {
          if (!x) {
            return this.gridView;
          }

          const filteredData = {
            data: x.data.filter(item =>
              (item.CostCodeCostTypeId !== GlobalCcctCombination.Tax) && (item.CostCodeCostTypeId !== GlobalCcctCombination.Contingency)
            ),
            total: x.total
          };
          this.gridView = filteredData;
          return filteredData;

        })
      );
  }

  private initializeFilter(): void {
    this.state.filter = {
      filters: [
        { field: 'ProjectTypeId', operator: 'eq', value: this.iProjectTypeId() },
        { field: 'IsActive', operator: 'eq', value: true }
      ],
      logic: 'and'
    };
  }

  public dataStateChange(state: DataStateChangeEvent): void {
    this.state = state;
    this.ccctGridService.query(state);
  }

  public phaseStepSortChange(sort: SortDescriptor[]): void {
    this.phaseStepSort = sort;
    this.iDistinctPhaseStep().splice(0, this.iDistinctPhaseStep().length, ...orderBy(this.iDistinctPhaseStep(), sort));
  }

  public phaseSelectionChange(event: SelectionEvent): void {
    event.selectedRows.forEach((row) => {
      const key = row.dataItem?.PSKey as string;
      if (key && !this.selectedPhaseStepKey.includes(key)) {
        this.selectedPhaseStepKey.push(key);
      }
    });

    event.deselectedRows.forEach((row) => {
      const key = row.dataItem?.PSKey as string;
      this.selectedPhaseStepKey = this.selectedPhaseStepKey.filter(k => k !== key);
    });
  }

  public selectionChange(event: SelectionEvent) {
    event.selectedRows.forEach((row) => {
      this.selectedItems.push(row);
    });

    event.deselectedRows.forEach((row) => {
      this.selectedItems = this.selectedItems.filter(f => f.index !== row.index);
    });
  }

  public addSelected(): void {
    let costItems = this.selectedItems.map(m => m.dataItem);
    let phaseSteps = this.selectedPhaseStepKey;
    this.addCcctItem.emit({ costItems: costItems, phaseSteps: phaseSteps });

    this.clearSelections();
  }

  public close() {
    this.clearSelections();
    this.closeDialog.emit(null);
  }

  public disableAdd(): boolean {
    if (this.showPhaseStep()) {
      return (this.selectedItems.length === 0 || this.selectedPhaseStepKey.length === 0);
    }
    else {
      return (this.selectedItems.length === 0);
    }
  }

  private clearSelections(): void {
    this.selectedItems = [];
    this.selectedPhaseStepKey = [];
  }
}